import { useLayoutEffect, useRef, useState, useCallback } from "react";

// Option B: renders a single continuous graphic (solid / gradient / image) behind
// multi-line text, masked to the per-line bounding boxes measured via Range.getClientRects().
// The graphic is anchored to the whole paragraph, so a pattern/gradient flows
// continuously top->bottom while each line only reveals its own slice (ragged blob shape).
// Reusable + optional: pass a `graphic` config object.
export function MaskedTextHighlight({
  text,
  graphic = {},
  as: Tag = "p",
  className = "",
  textColor = "#52525B",
  textShadow = "none",
  shape = "block",
  padX = 8,
  extraPadY = 1,
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [rects, setRects] = useState([]);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const cbox = container.getBoundingClientRect();
    const lineHeightPx = parseFloat(getComputedStyle(textEl).lineHeight) || 0;

    const range = document.createRange();
    range.selectNodeContents(textEl);
    const lineRects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0);

    const mapped = lineRects.map((r) => {
      const padY = Math.max(0, (lineHeightPx - r.height) / 2) + extraPadY;
      const left = r.left - cbox.left - padX;
      const top = r.top - cbox.top - padY;
      return {
        left,
        top,
        width: r.width + padX * 2,
        height: r.height + padY * 2,
      };
    });

    setBox({ w: cbox.width, h: cbox.height });
    setRects(mapped);
  }, [padX, extraPadY]);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", measure);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, text]);

  const {
    variant = "gradient",
    color = "#ffffff",
    opacity = 0.75,
    gradient_from = "#ffffff",
    gradient_to = "#f5f3ff",
    gradient_angle = 135,
    radius = 8,
    image_url = "",
  } = graphic;

  const fillBackground = () => {
    if (variant === "image" && image_url) return `url(${image_url})`;
    if (variant === "gradient") return `linear-gradient(${gradient_angle}deg, ${gradient_from}, ${gradient_to})`;
    return color;
  };

  const bg = fillBackground();

  // Single-block mode: one continuous graphic covering the whole paragraph box.
  if (shape === "block") {
    return (
      <Tag ref={containerRef} className={`relative ${className}`} style={{ color: textColor }}>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            zIndex: 0,
            borderRadius: radius,
            opacity,
            background: bg,
            backgroundImage: variant === "image" ? bg : undefined,
            backgroundSize: variant === "image" ? "cover" : undefined,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <span
          ref={textRef}
          style={{ position: "relative", zIndex: 1, textShadow, display: "block", padding: `${padX * 0.9}px ${padX * 1.6}px` }}
        >
          {text}
        </span>
      </Tag>
    );
  }

  return (
    <Tag ref={containerRef} className={`relative ${className}`} style={{ color: textColor }}>
      {/* Continuous graphic layer, clipped to each line box */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ zIndex: 0 }}>
        {rects.map((r, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: r.left,
              top: r.top,
              width: r.width,
              height: r.height,
              borderRadius: radius,
              opacity,
              background: bg,
              backgroundImage: variant === "image" ? bg : undefined,
              backgroundSize: `${box.w}px ${box.h}px`,
              backgroundPosition: `${-r.left}px ${-r.top}px`,
              backgroundRepeat: "no-repeat",
            }}
          />
        ))}
      </span>
      <span ref={textRef} style={{ position: "relative", zIndex: 1, textShadow }}>
        {text}
      </span>
    </Tag>
  );
}
